import {
    PriceAndPromotionInfo,
    PriceDisplayData,
    PricesDisplayData,
    SelectedPriceData
} from "../types/PriceAndPromotionInfo";
import {
    ConfiguredPrice,
    InternalDisplayConfig,
    PriceAndPromotionInfos,
    PrimaryPriceType
} from "../types/ArticleCardProps";
import { BettyBundle } from "../types/EvaluateArticleNew";

type AdditionalArgs = {
    dontShowUnitPriceForBundle: boolean;
    includeMarginTaxes: boolean;
};

export function includeMarginTaxesInPrice(internalDisplayConfig: InternalDisplayConfig | null): boolean {
    return !!internalDisplayConfig?.includeMarginTaxes;
}

export function primaryIsGross(
    primaryPriceType: PrimaryPriceType | null,
    configuredPrimaryPrice: ConfiguredPrice | undefined
): boolean {
    switch (primaryPriceType) {
        case "net":
            return false;
        case "gross":
            return true;
        default:
            return !!configuredPrimaryPrice?.toLocaleLowerCase()?.match(/gross/);
    }
}

function selectPriceData(type: string, price: PriceAndPromotionInfo, args: AdditionalArgs): SelectedPriceData | null {
    if (typeof typedHandlers[type] === "function") {
        return typedHandlers[type](price, args);
    }
    return null;
}

export function getPriceInfoToDisplay(
    price: PriceAndPromotionInfo | null,
    internalDisplayConfig: InternalDisplayConfig | null
): PriceDisplayData | null {
    if (price && internalDisplayConfig && price.valid && price.currency) {
        const includeMarginTaxes = includeMarginTaxesInPrice(internalDisplayConfig);
        const dontShowUnitPriceForBundle = internalDisplayConfig?.dontShowUnitPriceForBundle || false;
        const useGross = primaryIsGross(
            internalDisplayConfig.primaryPriceType,
            internalDisplayConfig.configuredPrimaryPrice
        );
        const args: AdditionalArgs = { dontShowUnitPriceForBundle, includeMarginTaxes };

        return {
            currency: price.currency,
            primary: selectPriceData(internalDisplayConfig.configuredPrimaryPrice, price, args),
            secondary: selectPriceData(internalDisplayConfig.configuredSecondaryPrice, price, args),
            piece: selectPriceData(useGross ? "pieceGross" : "pieceNet", price, args),
            priceHiddenReason: price.priceHiddenReason ?? null
        };
    }
    return null;
}

function getPriceDisplayData(
    bundle: BettyBundle,
    priceAndPromotionInfos: PriceAndPromotionInfos,
    internalDisplayConfig: InternalDisplayConfig
): PriceDisplayData | null {
    const bettyBundleId = bundle?.bettyBundleId?.bettyBundleId;
    if (bettyBundleId) {
        const priceAndPromotionInfo = priceAndPromotionInfos?.[bettyBundleId] || null;
        return getPriceInfoToDisplay(priceAndPromotionInfo, internalDisplayConfig);
    }
    return null;
}

export function getPricesDisplayData(
    bundleSelector: Record<string, string>,
    bundles: Record<string, BettyBundle> | undefined,
    priceAndPromotionInfos: PriceAndPromotionInfos,
    internalDisplayConfig: InternalDisplayConfig
): PricesDisplayData | null {
    const pricesDisplayData: PricesDisplayData = {};
    if (bundleSelector && bundles) {
        for (const bundleNumber of Object.keys(bundleSelector || {})) {
            const bundle = bundles[bundleNumber];
            pricesDisplayData[bundleNumber] = getPriceDisplayData(
                bundle,
                priceAndPromotionInfos,
                internalDisplayConfig
            );
        }
        return pricesDisplayData;
    }
    return null;
}

// This function aggregates some strings providing additional information about prices, which can be
// used as scss class names when rendering prices so that the shown prices can be styled correspondingly.
// possible combos: cip && dnr, promotion && dnr
export function getPriceStyle(price: PriceAndPromotionInfo) {
    // NOTE: the order of ifs is important
    if (price.isManualPrice) {
        return "manual";
    }
    if (price.isCipBbp) {
        return "cip";
    }

    const isCip = price.isCip;
    const isPromotion = price.isPromotion;
    const isDnr = price.isDnr;
    const isActiveDnr = price.isDnrActive;

    if (isCip && !isActiveDnr) {
        return "cip";
    }

    const classes = [];

    if (price.strikeThroughNet || isPromotion || isActiveDnr) {
        classes.push("promotion");
    }

    if (isDnr) {
        classes.push("volume-discount");
    }

    if (isActiveDnr) {
        classes.push("active");
    }

    return classes.join(" ");
}

// E.g. calling `handlers["unitNet"](somePriceInfo, someAdditionalArgs)` will then execute
// e.g. `unitNet(somePriceInfo, someAdditionalArgs)`, thus extracting the price info from
// somePriceInfo belonging to "unitNet" (where "unitNet" comes originally from the "priceConfig").
const handlers = {
    unitNet(price: PriceAndPromotionInfo, { includeMarginTaxes }: AdditionalArgs) {
        return {
            price: includeMarginTaxes ? price.unitNetWithTaxes : price.unitNet,
            strikeThrough: price.strikeThroughNet,
            style: getPriceStyle(price),
            info: "PRICE_WITH_LABEL_NET",
            unitIndicatorWeight: "EX_ARTICLE_KG"
        };
    },

    unitGross(price: PriceAndPromotionInfo, { includeMarginTaxes }: AdditionalArgs) {
        return {
            price: includeMarginTaxes ? price.unitGrossWithTaxes : price.unitGross,
            strikeThrough: price.strikeThroughGross,
            style: getPriceStyle(price),
            info: "PRICE_WITH_LABEL_GROSS",
            unitIndicatorWeight: "EX_ARTICLE_KG"
        };
    },

    unitGrossPriceOnly(price: PriceAndPromotionInfo, { includeMarginTaxes }: AdditionalArgs) {
        return {
            price: includeMarginTaxes ? price.unitGrossWithTaxes : price.unitGross,
            strikeThrough: price.strikeThroughGross,
            style: getPriceStyle(price),
            info: ""
        };
    },

    pieceNet(price: PriceAndPromotionInfo) {
        return {
            price: price.pieceNet,
            strikeThrough: null, // does not exist yet
            style: getPriceStyle(price),
            info: "PRICE_WITH_LABEL_PIECE_NET",
            unitIndicatorWeight: "EX_ARTICLE_PIECE"
        };
    },

    pieceGross(price: PriceAndPromotionInfo) {
        return {
            price: price.pieceGross,
            strikeThrough: null, // does not exist yet
            style: getPriceStyle(price),
            info: "PRICE_WITH_LABEL_PIECE_GROSS",
            unitIndicatorWeight: "EX_ARTICLE_PIECE"
        };
    },

    perKgLNet(price: PriceAndPromotionInfo) {
        return {
            price: price.kgNet,
            strikeThrough: null, // does not exist yet
            style: getPriceStyle(price),
            info: "PRICE_WITH_LABEL_PER_KG_L_NET",
            unitIndicatorWeight: "EX_ARTICLE_KG"
        };
    },

    // Either use the price per Kg or the piece price if that is not set
    perUnitNet(price: PriceAndPromotionInfo, { dontShowUnitPriceForBundle }: AdditionalArgs) {
        const perKgLNet = handlers.perKgLNet(price);
        const pieceNet = handlers.pieceNet(price);

        if (perKgLNet.price !== undefined && perKgLNet.price !== null) {
            return perKgLNet;
        }
        if (dontShowUnitPriceForBundle) {
            return null;
        } else {
            return pieceNet;
        }
    },

    perKgLGross(price: PriceAndPromotionInfo) {
        return {
            price: price.kgGross,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: getPriceStyle(price),
            info: "PRICE_WITH_LABEL_PER_KG_L_GROSS",
            unitIndicatorWeight: "EX_ARTICLE_KG"
        };
    },

    depositNet(price: PriceAndPromotionInfo) {
        return {
            price: price.depositNet,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: "deposit",
            info: "PRICE_WITH_LABEL_DEPOSIT_NET"
        };
    },

    depositGross(price: PriceAndPromotionInfo) {
        return {
            price: price.depositGross,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: "deposit vat",
            info: "PRICE_WITH_LABEL_DEPOSIT_GROSS"
        };
    },

    serviceFeeNet(price: PriceAndPromotionInfo) {
        return {
            price: null, // does not yet exist
            strikeThrough: null, // does not exist yet
            style: getPriceStyle(price),
            renderDescriptionOnly: false,
            info: "PRICE_WITH_LABEL_SERVICE_FEE_NET"
        };
    },

    serviceFeeGross(price: PriceAndPromotionInfo) {
        return {
            price: null, // does not yet exist
            strikeThrough: null, // does not exist yet
            style: getPriceStyle(price),
            renderDescriptionOnly: false,
            info: "PRICE_WITH_LABEL_SERVICE_FEE_GROSS"
        };
    },

    vatIncludedHint(price: PriceAndPromotionInfo) {
        return {
            price: null,
            strikeThrough: null, // does not exist yet
            style: getPriceStyle(price),
            renderDescriptionOnly: true,
            info: "VAT_INCLUDED_PRICE_HINT"
        };
    }
};

// This makes sure that the handler functions all take the same parameters and can be generically called in selectPriceData.
// Still keeping the `handlers` object separately allows us to call functions from it directly and leave out the parameters that that particular function doesn't need.
const typedHandlers: {
    [x: string]: (x0: PriceAndPromotionInfo, args: AdditionalArgs) => SelectedPriceData | null;
} = handlers;
